/**
 * Reorganiza os blocos do cartão de produto: move a galeria para
 * .product-card__media, categoria/título/preço para .product-card__header e
 * variantes/botões para .product-card__footer, esvaziando .product-card__blocks.
 *
 * Notas de implementação (a versão anterior vivia inline em snippets/product-card.liquid
 * e era duplicada uma vez por cartão, com um MutationObserver e um setInterval cada):
 *
 * 1. Só organiza cartões já totalmente lidos pelo parser. Organizar um cartão a meio
 *    do streaming do HTML removia o .product-card__blocks e o parser continuava a
 *    inserir os blocos seguintes (categoria, título, variantes, botão) num nó já
 *    destacado do DOM - o cartão ficava só com a imagem.
 * 2. O atributo data-organized (que esconde o contentor via CSS) só é aplicado depois
 *    de os blocos serem movidos, nunca antes.
 * 3. Um único observer e um único intervalo por página.
 */
(function () {
  'use strict';

  /**
   * Um cartão só está completo quando o parser fechou a tag: nessa altura já existe
   * um nó a seguir (o próprio cartão ou o <li> que o contém). Depois de o documento
   * deixar de estar em 'loading' está tudo garantidamente completo.
   * @param {Element} card
   * @returns {boolean}
   */
  function isFullyParsed(card) {
    if (document.readyState !== 'loading') return true;
    if (card.nextSibling) return true;
    return Boolean(card.parentElement && card.parentElement.nextSibling);
  }

  /**
   * @param {Element} card
   */
  function organizeProductCard(card) {
    const blocksContainer = card.querySelector('[data-product-card-blocks]');
    if (!blocksContainer) return;
    if (!isFullyParsed(card)) return;

    const mediaContainer = card.querySelector('.product-card__media');
    const headerContainer = card.querySelector('.product-card__header');
    const footerContainer = card.querySelector('.product-card__footer');

    if (!mediaContainer || !headerContainer || !footerContainer) {
      // Sem os contentores de destino, mostrar os blocos onde estão
      blocksContainer.style.display = 'block';
      return;
    }

    if (!blocksContainer.children.length) {
      card.setAttribute('data-organized', 'true');
      return;
    }

    // Galeria para o contentor de media
    const gallery = blocksContainer.querySelector('.card-gallery');
    if (gallery && gallery.parentElement === blocksContainer) {
      mediaContainer.appendChild(gallery);
    }

    // Categoria e título para o cabeçalho
    const category = blocksContainer.querySelector('.product-card-category');

    // O bloco product-title rende <a class="contents user-select-text" ref="productTitleLink">
    let titleLink = blocksContainer.querySelector('a.contents.user-select-text');
    if (!titleLink) {
      const textBlock = blocksContainer.querySelector('.text-block');
      if (textBlock) titleLink = textBlock.closest('a');
    }

    let title = null;
    if (titleLink) {
      // Subir até ao filho directo de blocksContainer
      let current = titleLink;
      while (current && current.parentElement && current.parentElement !== blocksContainer) {
        current = current.parentElement;
      }
      if (current && current.parentElement === blocksContainer) {
        title = current;
      } else if (titleLink.parentElement === blocksContainer) {
        title = titleLink;
      }
    }

    if (category && category.parentElement === blocksContainer) {
      headerContainer.appendChild(category);
    }
    if (title && title.parentElement === blocksContainer) {
      headerContainer.appendChild(title);
    }

    const price = blocksContainer.querySelector('product-price, .price-block, [class*="price"]');
    if (price && price.parentElement === blocksContainer) {
      headerContainer.appendChild(price);
    }

    // Variantes e botões para o rodapé
    const variants = blocksContainer.querySelector('product-card-variants');
    const buyButtons = blocksContainer.querySelector('.buy-buttons-block');
    if (variants && variants.parentElement === blocksContainer) {
      footerContainer.appendChild(variants);
    }
    if (buyButtons && buyButtons.parentElement === blocksContainer) {
      footerContainer.appendChild(buyButtons);
    }

    // Tudo o que sobrar vai para o cabeçalho, para nada ficar escondido
    Array.from(blocksContainer.children).forEach(function (child) {
      if (child.parentElement === blocksContainer) headerContainer.appendChild(child);
    });

    // Só agora se marca como organizado: é este atributo que esconde o contentor
    card.setAttribute('data-organized', 'true');

    if (blocksContainer.children.length === 0) {
      blocksContainer.remove();
    } else {
      blocksContainer.style.display = 'block';
      card.setAttribute('data-partial', 'true');
    }
  }

  function initProductCards() {
    document.querySelectorAll('product-card').forEach(organizeProductCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initProductCards();
      setTimeout(initProductCards, 100);
      setTimeout(initProductCards, 500);
    });
  } else {
    initProductCards();
    setTimeout(initProductCards, 100);
    setTimeout(initProductCards, 500);
  }

  // Cartões adicionados dinamicamente (filtros, paginação, quick add)
  let pending = false;
  const observer = new MutationObserver(function (mutations) {
    if (pending) return;

    const relevant = mutations.some(function (mutation) {
      if (mutation.type !== 'childList' || !mutation.addedNodes.length) return false;
      if (mutation.target.hasAttribute && mutation.target.hasAttribute('data-product-card-blocks')) return true;
      return Array.from(mutation.addedNodes).some(function (node) {
        if (node.nodeType !== 1) return false;
        return node.tagName === 'PRODUCT-CARD' || (node.querySelector && node.querySelector('product-card'));
      });
    });

    if (!relevant) return;

    pending = true;
    setTimeout(function () {
      pending = false;
      initProductCards();
    }, 50);
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  // Rede de segurança para cartões que tenham escapado
  setInterval(function () {
    document.querySelectorAll('product-card:not([data-organized])').forEach(organizeProductCard);
  }, 2000);
})();
